import { useCallback, useEffect, useState } from 'react'

import { useTrackBalanceEvent } from '@features/balanceLedger'

import type { PeriodMode } from '../../../../../enums/periodMode'

interface UseTrackSuppliersBalanceDetailViewProps {
  accountId?: string
  // True until `resultsCount` reflects the current search/period params — covers keepPreviousData still serving
  // stale rows, the initial fetch (nothing to keep yet), and an errored fetch with no cache to fall back on.
  isResultsUnsettled: boolean
  periodType?: PeriodMode
  resultsCount: number
  // The search currently in the URL. The armed length can update a render before this does, and the event has to
  // wait for them to match so it does not report the previous statement's count.
  searchQuery?: string
}

interface UseTrackSuppliersBalanceDetailViewResult {
  // Arm `Detail Search Used` from a real search interaction only — never from URL hydration — so a shared link that
  // already carries `ledgerSearchQuery` does not look like a user search (same pattern as overview filter/search).
  armSearchTracking: (query?: string) => void
  trackBackClick: () => void
}

/**
 * Owns the two detail-view Segment events. `Back Clicked` fires immediately; `Detail Search Used` waits for
 * `isResultsUnsettled` to clear so it reports a truthful `resultsCount`. Pending search is armed only via
 * `armSearchTracking` (called from the search handler), not by watching `ledgerSearchQuery` itself.
 */
export const useTrackSuppliersBalanceDetailView = ({
  accountId,
  isResultsUnsettled,
  periodType,
  resultsCount,
  searchQuery,
}: UseTrackSuppliersBalanceDetailViewProps): UseTrackSuppliersBalanceDetailViewResult => {
  const trackBalanceEvent = useTrackBalanceEvent()
  const [pendingSearchQueryLength, setPendingSearchQueryLength] = useState<number>()

  const armSearchTracking = useCallback((query?: string) => {
    // Clearing (directly, or as a side effect of a period change) leaves no settled result to report, so drop any
    // length still awaiting its fetch to settle rather than letting it fire later against a stale `resultsCount`.
    setPendingSearchQueryLength(query ? query.length : undefined)
  }, [])

  useEffect(() => {
    const activeSearchQueryLength = searchQuery?.length ?? 0
    if (
      pendingSearchQueryLength === undefined ||
      isResultsUnsettled ||
      activeSearchQueryLength !== pendingSearchQueryLength
    ) {
      return
    }

    /* eslint-disable @typescript-eslint/naming-convention */
    trackBalanceEvent('Detail Search Used', {
      account_id: accountId,
      period_type: periodType,
      results_count: resultsCount,
      search_query_length: pendingSearchQueryLength,
    })
    /* eslint-enable @typescript-eslint/naming-convention */
    setPendingSearchQueryLength(undefined)
  }, [
    accountId,
    isResultsUnsettled,
    pendingSearchQueryLength,
    periodType,
    resultsCount,
    searchQuery,
    trackBalanceEvent,
  ])

  const trackBackClick = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    trackBalanceEvent('Back Clicked', { account_id: accountId, period_type: periodType })
  }, [accountId, periodType, trackBalanceEvent])

  return { armSearchTracking, trackBackClick }
}
